/**
 * DecisionFlow — KickerPlanManager
 * data-fit LLC · Confidential
 * Domain: P3-02 Kicker / Multiplier Plans (Standard tier — feature_gate_key = NULL)
 * Spec authority: decisionflow_phase3_plan_spec_v1_0.md §3
 * API Contract: api_contract_v1_32.md §49
 * Wizard substages:
 *   kicker_plan_definition · kicker_approval · kicker_validation
 *
 * Flow:
 *   List → select plan_component → list kicker plans for that component
 *   → Create plan → add rules (≥1 required before submit) → submit → approve
 *   → Results tab: view kicker_result rows for a calculation run
 *
 * DDL ground truth (phase3_plan_spec §3.2):
 *   kicker_plan:
 *     kicker_plan_id bigint PK, plan_component_id int FK, plan_name varchar(200),
 *     plan_description text, applies_to varchar(30) CHECK(total_payout|component_payout),
 *     effective_from date, effective_to date, is_active bool,
 *     submitted_by uuid, submitted_at timestamptz,
 *     approved_by uuid, approved_at timestamptz,
 *     created_by uuid, created_at timestamptz, updated_at timestamptz
 *   kicker_rule:
 *     kicker_rule_id bigint PK, kicker_plan_id bigint FK,
 *     rule_name varchar(200), rule_sequence smallint,
 *     metric varchar(60) CHECK(attainment_pct|revenue_type_attainment|total_credit|component_payout),
 *     metric_qualifier varchar(30) — revenue_type_code when metric=revenue_type_attainment; NULL otherwise,
 *     operator varchar(10) CHECK(gte|gt|lte|lt|eq|between),
 *     threshold_value numeric(12,6), threshold_value_2 numeric(12,6) — required when operator=between,
 *     multiplier_value numeric(8,6), condition_logic varchar(10) CHECK(AND|OR),
 *     is_active bool, created_by uuid, created_at timestamptz, updated_at timestamptz
 *   kicker_result:
 *     kicker_result_id uuid PK, calculation_run_id uuid, position_id uuid,
 *     fiscal_period_id int, kicker_plan_id bigint, kicker_rule_id bigint,
 *     rule_fired bool, metric_value numeric(12,6), threshold_met bool,
 *     multiplier_applied numeric(8,6), payout_before numeric(18,4), payout_after numeric(18,4)
 *
 * API surface (api_contract_v1_32.md §49):
 *   POST   /api/v1/kicker-plans
 *   GET    /api/v1/kicker-plans?plan_component_id={id}
 *   GET    /api/v1/kicker-plans/{kicker_plan_id}         — returns plan + rules[]
 *   PUT    /api/v1/kicker-plans/{kicker_plan_id}         — raw fetch PATCH-style
 *   POST   /api/v1/kicker-plans/{kicker_plan_id}/submit
 *   POST   /api/v1/kicker-plans/{kicker_plan_id}/approve
 *   POST   /api/v1/kicker-plans/{kicker_plan_id}/rules
 *   PUT    /api/v1/kicker-plans/{kicker_plan_id}/rules/{kicker_rule_id}
 *   DELETE /api/v1/kicker-plans/{kicker_plan_id}/rules/{kicker_rule_id}
 *   GET    /api/v1/kicker-results?calculation_run_id={id}&position_id={id}
 *
 * Architecture rules (ARCH-2, NN-36, token-compliant CSS):
 *   - useAuth() for { user } — token never in props
 *   - apiGet / apiPost from client — PUT/DELETE via raw fetch
 *   - tenant_id never in any request body (from JWT only)
 *   - No window.prompt / window.confirm — inline confirmation state
 *   - ApprovalPanel: submitted_by IS a real column on kicker_plan (Phase 3 DDL)
 *   - onReject={null} — reject endpoint not in §49
 *   - Self-approval: currentUserId vs submittedBy checked by ApprovalPanel
 *   - GateStatusBanner for 403 FEATURE_UNAVAILABLE
 *
 * Props:
 *   mode             — 'wizard' | 'admin'
 *   period           — { fiscal_period_id, period_code } | null
 *   wizardSteps      — array
 *   embedded         — bool  — when true: context injection mode (spec §4.8)
 *   planId           — int   — injected plan id (embedded only)
 *   planComponentId  — int   — injected component id, skip selector
 *   fiscalPeriodId   — int   — injected fiscal period (embedded only)
 *   readOnly         — bool  — true when plan is approved/archived of substage step objects (wizard context only)
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../auth/AuthContext.jsx';
import { useFeatures } from '../../auth/FeatureContext.jsx';
import { apiGet, apiPost } from '../../api/client';
import ApprovalPanel from '../shared/ApprovalPanel.jsx';
import GateStatusBanner from '../shared/GateStatusBanner.jsx';
import StatusPill from '../shared/StatusPill.jsx';

// ---------------------------------------------------------------------------
// Constants — DDL-authoritative enum values
// ---------------------------------------------------------------------------

const APPLIES_TO_OPTIONS = [
  { value: 'total_payout',     label: 'Total payout' },
  { value: 'component_payout', label: 'Component payout' },
];

const METRIC_OPTIONS = [
  { value: 'attainment_pct',           label: 'Overall attainment %' },
  { value: 'revenue_type_attainment',  label: 'Revenue-type attainment %' },
  { value: 'total_credit',             label: 'Total credit (reporting currency)' },
  { value: 'component_payout',         label: 'Component payout amount' },
];

const OPERATOR_OPTIONS = [
  { value: 'gte',     label: '≥  (at least)' },
  { value: 'gt',      label: '>  (more than)' },
  { value: 'lte',     label: '≤  (at most)' },
  { value: 'lt',      label: '<  (less than)' },
  { value: 'eq',      label: '=  (exactly)' },
  { value: 'between', label: 'between (range)' },
];

const CONDITION_LOGIC_OPTIONS = [
  { value: 'AND', label: 'AND — all conditions must be met' },
  { value: 'OR',  label: 'OR — any condition fires the kicker' },
];

// Revenue type codes for metric_qualifier (mirrors ref_revenue_type seed — allow_on_import = TRUE)
const REVENUE_TYPE_CODES = [
  { value: 'new_logo',       label: 'New Logo' },
  { value: 'expansion',      label: 'Expansion' },
  { value: 'renewal',        label: 'Renewal' },
  { value: 'upsell',         label: 'Upsell' },
  { value: 'churn_recovery', label: 'Churn Recovery' },
];

const TABS = [
  { key: 'plans',   label: 'Kicker Plans'  },
  { key: 'results', label: 'Run Results'   },
];

const EMPTY_PLAN_FORM = {
  plan_name:        '',
  plan_description: '',
  applies_to:       'total_payout',
  effective_from:   '',
  effective_to:     '',
};

const EMPTY_RULE_FORM = {
  rule_name:         '',
  rule_sequence:     '1',
  metric:            'attainment_pct',
  metric_qualifier:  '',
  operator:          'gte',
  threshold_value:   '',
  threshold_value_2: '',
  multiplier_value:  '',
  condition_logic:   'AND',
};

// ---------------------------------------------------------------------------
// Shared CSS class strings — token-compliant
// ---------------------------------------------------------------------------

const thClass =
  'px-3 py-2 text-left text-tok-xs font-semibold uppercase tracking-wide ' +
  'text-[rgb(var(--color-text-muted))] bg-[rgb(var(--color-surface-muted))] ' +
  'border-b border-[rgb(var(--color-border))]';

const tdClass =
  'px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] ' +
  'border-b border-[rgb(var(--color-border))]';

const btnPrimary =
  'rounded-lg bg-[rgb(var(--color-primary))] px-3 py-1.5 text-tok-sm font-medium ' +
  'text-white hover:bg-[rgb(var(--color-primary-hover))] ' +
  'disabled:opacity-50 disabled:cursor-not-allowed';

const btnSecondary =
  'rounded-lg bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] ' +
  'px-3 py-1.5 text-tok-sm font-medium text-[rgb(var(--color-text))] ' +
  'hover:bg-[rgb(var(--color-surface-muted))] ' +
  'disabled:opacity-50 disabled:cursor-not-allowed';

const btnDanger =
  'rounded-lg border border-[rgb(var(--color-danger))]/40 px-3 py-1.5 text-tok-sm ' +
  'font-medium text-[rgb(var(--color-danger))] hover:bg-[rgb(var(--color-danger))]/10 ' +
  'disabled:opacity-50 disabled:cursor-not-allowed';

const inputClass =
  'mt-1 block w-full rounded-lg border border-[rgb(var(--color-border))] ' +
  'bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text))] ' +
  'px-3 py-2 text-tok-sm focus:outline-none ' +
  'focus:ring-2 focus:ring-[rgb(var(--color-focus-ring))]';

const labelClass = 'flex flex-col text-tok-sm font-medium text-[rgb(var(--color-text))]';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function deriveState(entity) {
  if (!entity) return 'draft';
  if (entity.approved_by)                         return 'approved';
  if (entity.submitted_at || entity.submitted_by) return 'pending_approval';
  return 'draft';
}

function getErrorMessage(error, fallback) {
  return error?.message || error?.detail || fallback;
}

function getApiErrorCode(error) {
  return error?.code || null;
}

function fmtMoney(val) {
  if (val === null || val === undefined || val === '') return '—';
  return Number(val).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtMultiplier(val) {
  if (val === null || val === undefined) return '—';
  return `×${Number(val).toFixed(4)}`;
}

function fmtDate(val) {
  if (!val) return '—';
  return String(val).slice(0, 10);
}

function labelFor(options, value) {
  return options.find((o) => o.value === value)?.label || value || '—';
}

function formatThreshold(rule) {
  if (!rule) return '—';
  const needsPct = rule.metric === 'attainment_pct' || rule.metric === 'revenue_type_attainment';
  const fmt = (v) => needsPct ? `${(Number(v) * 100).toFixed(1)}%` : fmtMoney(v);
  if (rule.operator === 'between') {
    return `${fmt(rule.threshold_value)} – ${fmt(rule.threshold_value_2)}`;
  }
  return fmt(rule.threshold_value);
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function KickerPlanManager({ mode, period, wizardSteps, embedded = false, planId, planComponentId, fiscalPeriodId, readOnly = false }) {
  const { user } = useAuth();
  const { hasPlanType } = useFeatures();

  if (!hasPlanType('feature.plan.kicker')) {
    return <GateStatusBanner code="PLAN_TYPE_NOT_ENTITLED" />;
  }

  const [activeTab, setActiveTab]                   = useState('plans');
  const [featureUnavailable, setFeatureUnavailable] = useState(false);
  const [toast, setToast]                           = useState(null);

  // Plan component selector
  const [components, setComponents]                 = useState([]);
  const [componentsLoading, setComponentsLoading]   = useState(false);
  const [componentsError, setComponentsError]       = useState('');
  const [selectedComponentId, setSelectedComponentId] = useState('');

  // Kicker plans for selected component
  const [plans, setPlans]                           = useState([]);
  const [plansLoading, setPlansLoading]             = useState(false);
  const [plansError, setPlansError]                 = useState('');

  // Selected plan detail + rules
  const [selectedPlan, setSelectedPlan]             = useState(null);
  const [planRules, setPlanRules]                   = useState([]);
  const [detailLoading, setDetailLoading]           = useState(false);
  const [actionLoading, setActionLoading]           = useState(false);
  const [actionError, setActionError]               = useState('');

  // Plan create/edit form
  const [showPlanForm, setShowPlanForm]             = useState(false);
  const [planForm, setPlanForm]                     = useState(EMPTY_PLAN_FORM);
  const [planSaving, setPlanSaving]                 = useState(false);
  const [planFormError, setPlanFormError]           = useState('');
  const [editingPlanId, setEditingPlanId]           = useState(null);

  // Rule form
  const [showRuleForm, setShowRuleForm]             = useState(false);
  const [ruleForm, setRuleForm]                     = useState(EMPTY_RULE_FORM);
  const [ruleSaving, setRuleSaving]                 = useState(false);
  const [ruleError, setRuleError]                   = useState('');
  const [editingRuleId, setEditingRuleId]           = useState(null);
  const [confirmDeleteRuleId, setConfirmDeleteRuleId] = useState(null);

  // Results tab
  const [runIdInput, setRunIdInput]                 = useState('');
  const [positionIdInput, setPositionIdInput]       = useState('');
  const [results, setResults]                       = useState([]);
  const [resultsLoading, setResultsLoading]         = useState(false);
  const [resultsError, setResultsError]             = useState('');

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  const showToast = useCallback((message) => {
    setToast(message);
    window.setTimeout(() => setToast(null), 3000);
  }, []);

  const currentUserId = user?.user_account_id || null; // L-90: useAuth() returns user_account_id (S720)

  // ---------------------------------------------------------------------------
  // Load plan components for selector
  // ---------------------------------------------------------------------------

  const loadComponents = useCallback(async () => {
    if (!user) return;
    setComponentsLoading(true);
    setComponentsError('');
    try {
      // C7 — S689 plan_id scoping: scope component list to this plan when planId is injected.
      // Without this, managers show components from all plans on the tenant (AcceleratorCap pattern).
      const periodId = period?.fiscal_period_id;
      const params = new URLSearchParams();
      if (planId != null) params.set('plan_id', String(planId));
      if (periodId != null) params.set('fiscal_period_id', String(periodId));
      const url = `/api/v1/plan-components${params.toString() ? '?' + params.toString() : ''}`;
      const data = await apiGet(url);
      setComponents(Array.isArray(data) ? data : data?.data || data?.plan_components || []);
    } catch (error) {
      if (getApiErrorCode(error) === 'FEATURE_UNAVAILABLE' || error?.status === 403) {
        setFeatureUnavailable(true);
        return;
      }
      setComponentsError(getErrorMessage(error, 'Unable to load plan components.'));
    } finally {
      setComponentsLoading(false);
    }
  }, [user, period]);

  useEffect(() => { loadComponents(); }, [loadComponents]);

  // Embedded context injection (spec §4.8) — when embedded=true, planComponentId is injected
  useEffect(() => {
    if (embedded && planComponentId != null) {
      setSelectedComponentId(String(planComponentId));
    }
  }, [embedded, planComponentId]);

  // ---------------------------------------------------------------------------
  // Load kicker plans for selected component
  // ---------------------------------------------------------------------------

  const loadPlans = useCallback(async (componentId) => {
    if (!componentId) return;
    setPlansLoading(true);
    setPlansError('');
    setPlans([]);
    try {
      const data = await apiGet(`/api/v1/kicker-plans?plan_component_id=${componentId}`);
      setPlans(Array.isArray(data) ? data : data?.kicker_plans ?? data?.data ?? []);
    } catch (error) {
      setPlansError(getErrorMessage(error, 'Unable to load kicker plans.'));
    } finally {
      setPlansLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedComponentId) loadPlans(selectedComponentId);
  }, [selectedComponentId, loadPlans]);

  // ---------------------------------------------------------------------------
  // Load plan detail + rules
  // ---------------------------------------------------------------------------

  const loadPlanDetail = useCallback(async (planId) => {
    setDetailLoading(true);
    setActionError('');
    setPlanRules([]);
    try {
      const data = await apiGet(`/api/v1/kicker-plans/${planId}`);
      const plan = data?.kicker_plan ?? data;
      setSelectedPlan(plan);
      setPlanRules(Array.isArray(plan?.rules) ? plan.rules : []);
    } catch (error) {
      setActionError(getErrorMessage(error, 'Unable to load kicker plan detail.'));
    } finally {
      setDetailLoading(false);
    }
  }, []);

  // ---------------------------------------------------------------------------
  // Plan form helpers
  // ---------------------------------------------------------------------------

  const updatePlanForm = useCallback((field, value) =>
    setPlanForm((prev) => ({ ...prev, [field]: value })), []);

  const resetPlanForm = useCallback(() => {
    setPlanForm(EMPTY_PLAN_FORM);
    setPlanFormError('');
    setEditingPlanId(null);
  }, []);

  const validatePlanForm = useCallback(() => {
    if (!planForm.plan_name.trim())   return 'Plan name is required.';
    if (!planForm.applies_to)         return 'Applies-to is required.';
    if (!planForm.effective_from)     return 'Effective from date is required.';
    if (planForm.effective_to && planForm.effective_to < planForm.effective_from)
      return 'Effective to must be after effective from.';
    return '';
  }, [planForm]);

  // ---------------------------------------------------------------------------
  // Create plan
  // ---------------------------------------------------------------------------

  const createPlan = useCallback(async () => {
    if (!selectedComponentId) { setPlanFormError('Select a plan component first.'); return; }
    const err = validatePlanForm();
    if (err) { setPlanFormError(err); return; }
    setPlanSaving(true);
    setPlanFormError('');
    try {
      const payload = {
        plan_component_id: Number(selectedComponentId),
        plan_name:         planForm.plan_name.trim(),
        plan_description:  planForm.plan_description.trim() || null,
        applies_to:        planForm.applies_to,
        effective_from:    planForm.effective_from,
        effective_to:      planForm.effective_to || null,
      };
      const created = await apiPost('/api/v1/kicker-plans', payload);
      await loadPlans(selectedComponentId);
      setShowPlanForm(false);
      resetPlanForm();
      showToast('Kicker plan created.');
      const newId = created?.kicker_plan_id;
      if (newId) await loadPlanDetail(newId);
    } catch (error) {
      setPlanFormError(getErrorMessage(error, 'Unable to create kicker plan.'));
    } finally {
      setPlanSaving(false);
    }
  }, [loadPlanDetail, loadPlans, planForm, resetPlanForm, selectedComponentId, showToast, validatePlanForm]);

  // ---------------------------------------------------------------------------
  // Update plan (PUT — raw fetch, plan must be draft)
  // ---------------------------------------------------------------------------

  const updatePlan = useCallback(async () => {
    if (!editingPlanId) return;
    const err = validatePlanForm();
    if (err) { setPlanFormError(err); return; }
    setPlanSaving(true);
    setPlanFormError('');
    try {
      const res = await fetch(`/api/v1/kicker-plans/${editingPlanId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          plan_name:        planForm.plan_name.trim(),
          plan_description: planForm.plan_description.trim() || null,
          applies_to:       planForm.applies_to,
          effective_from:   planForm.effective_from,
          effective_to:     planForm.effective_to || null,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const code = body?.error?.code || body?.code;
        if (code === 'CONFIG_ALREADY_APPROVED') throw new Error('This plan is already approved and cannot be edited.');
        throw new Error(body?.error?.message || body?.detail || `Update failed (${res.status})`);
      }
      await loadPlanDetail(editingPlanId);
      await loadPlans(selectedComponentId);
      setShowPlanForm(false);
      resetPlanForm();
      showToast('Kicker plan updated.');
    } catch (error) {
      setPlanFormError(error.message || 'Unable to update kicker plan.');
    } finally {
      setPlanSaving(false);
    }
  }, [editingPlanId, loadPlanDetail, loadPlans, planForm, resetPlanForm, selectedComponentId, showToast, validatePlanForm]);

  const openEditForm = useCallback((plan) => {
    setEditingPlanId(plan.kicker_plan_id);
    setPlanForm({
      plan_name:        plan.plan_name || '',
      plan_description: plan.plan_description || '',
      applies_to:       plan.applies_to || 'total_payout',
      effective_from:   fmtDate(plan.effective_from),
      effective_to:     fmtDate(plan.effective_to) === '—' ? '' : fmtDate(plan.effective_to),
    });
    setPlanFormError('');
    setShowPlanForm(true);
  }, []);

  // ---------------------------------------------------------------------------
  // Approval actions
  // ---------------------------------------------------------------------------

  const submitPlan = useCallback(async () => {
    if (!selectedPlan) return;
    // Guard: must have at least one rule before submit
    if (planRules.length === 0) {
      setActionError('At least one rule is required before this plan can be submitted.');
      return;
    }
    setActionLoading(true);
    setActionError('');
    try {
      await apiPost(`/api/v1/kicker-plans/${selectedPlan.kicker_plan_id}/submit`, {});
      await loadPlanDetail(selectedPlan.kicker_plan_id);
      await loadPlans(selectedComponentId);
      showToast('Kicker plan submitted for approval.');
    } catch (error) {
      const code = getApiErrorCode(error);
      if (code === 'KICKER_PLAN_NO_RULES')
        setActionError('This plan has no rules. Add at least one rule before submitting.');
      else
        setActionError(getErrorMessage(error, 'Unable to submit kicker plan.'));
    } finally {
      setActionLoading(false);
    }
  }, [loadPlanDetail, loadPlans, planRules, selectedComponentId, selectedPlan, showToast]);

  const approvePlan = useCallback(async () => {
    if (!selectedPlan) return;
    setActionLoading(true);
    setActionError('');
    try {
      await apiPost(`/api/v1/kicker-plans/${selectedPlan.kicker_plan_id}/approve`, {});
      await loadPlanDetail(selectedPlan.kicker_plan_id);
      await loadPlans(selectedComponentId);
      showToast('Kicker plan approved.');
    } catch (error) {
      const code = getApiErrorCode(error);
      if (code === 'SELF_APPROVAL_BLOCKED')
        setActionError('You submitted this plan — a different user must approve it.');
      else
        setActionError(getErrorMessage(error, 'Unable to approve kicker plan.'));
    } finally {
      setActionLoading(false);
    }
  }, [loadPlanDetail, loadPlans, selectedComponentId, selectedPlan, showToast]);

  // ---------------------------------------------------------------------------
  // Rule form helpers
  // ---------------------------------------------------------------------------

  const updateRuleForm = useCallback((field, value) =>
    setRuleForm((prev) => ({ ...prev, [field]: value })), []);

  const resetRuleForm = useCallback(() => {
    setRuleForm(EMPTY_RULE_FORM);
    setRuleError('');
    setEditingRuleId(null);
  }, []);

  const validateRuleForm = useCallback(() => {
    if (!ruleForm.rule_name.trim())  return 'Rule name is required.';
    const seq = Number(ruleForm.rule_sequence);
    if (!Number.isInteger(seq) || seq < 1) return 'Rule sequence must be a positive integer.';
    if (!ruleForm.metric)            return 'Metric is required.';
    if (ruleForm.metric === 'revenue_type_attainment' && !ruleForm.metric_qualifier)
      return 'Revenue type is required when metric is revenue-type attainment.';
    if (!ruleForm.operator)          return 'Operator is required.';
    if (!ruleForm.threshold_value || isNaN(Number(ruleForm.threshold_value)))
      return 'Threshold value must be a number.';
    if (ruleForm.operator === 'between') {
      if (!ruleForm.threshold_value_2 || isNaN(Number(ruleForm.threshold_value_2)))
        return 'Upper threshold is required for the between operator.';
      if (Number(ruleForm.threshold_value_2) <= Number(ruleForm.threshold_value))
        return 'Upper threshold must be greater than the lower threshold.';
    }
    if (!ruleForm.multiplier_value || isNaN(Number(ruleForm.multiplier_value)) || Number(ruleForm.multiplier_value) <= 0)
      return 'Multiplier must be a positive number (e.g. 1.15 for a 15% boost).';
    if (!ruleForm.condition_logic)   return 'Condition logic (AND / OR) is required.';
    return '';
  }, [ruleForm]);

  // ---------------------------------------------------------------------------
  // Add rule
  // ---------------------------------------------------------------------------

  const addRule = useCallback(async () => {
    if (!selectedPlan) return;
    const err = validateRuleForm();
    if (err) { setRuleError(err); return; }
    setRuleSaving(true);
    setRuleError('');
    try {
      const payload = {
        rule_name:         ruleForm.rule_name.trim(),
        rule_sequence:     Number(ruleForm.rule_sequence),
        metric:            ruleForm.metric,
        metric_qualifier:  ruleForm.metric === 'revenue_type_attainment'
                             ? ruleForm.metric_qualifier
                             : null,
        operator:          ruleForm.operator,
        threshold_value:   ruleForm.threshold_value,
        threshold_value_2: ruleForm.operator === 'between' ? ruleForm.threshold_value_2 : null,
        multiplier_value:  ruleForm.multiplier_value,
        condition_logic:   ruleForm.condition_logic,
      };
      await apiPost(`/api/v1/kicker-plans/${selectedPlan.kicker_plan_id}/rules`, payload);
      await loadPlanDetail(selectedPlan.kicker_plan_id);
      resetRuleForm();
      setShowRuleForm(false);
      showToast('Rule added.');
    } catch (error) {
      const code = getApiErrorCode(error);
      if (code === 'KICKER_RULE_SEQUENCE_CONFLICT')
        setRuleError(`Rule sequence ${ruleForm.rule_sequence} already exists. Choose a different sequence number.`);
      else if (code === 'KICKER_MULTIPLIER_INVALID')
        setRuleError('Multiplier must be greater than zero.');
      else
        setRuleError(getErrorMessage(error, 'Unable to add rule.'));
    } finally {
      setRuleSaving(false);
    }
  }, [loadPlanDetail, resetRuleForm, ruleForm, selectedPlan, showToast, validateRuleForm]);

  // ---------------------------------------------------------------------------
  // Update rule (PUT — raw fetch)
  // ---------------------------------------------------------------------------

  const updateRule = useCallback(async () => {
    if (!selectedPlan || !editingRuleId) return;
    const err = validateRuleForm();
    if (err) { setRuleError(err); return; }
    setRuleSaving(true);
    setRuleError('');
    try {
      const res = await fetch(
        `/api/v1/kicker-plans/${selectedPlan.kicker_plan_id}/rules/${editingRuleId}`,
        {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            rule_name:         ruleForm.rule_name.trim(),
            rule_sequence:     Number(ruleForm.rule_sequence),
            metric:            ruleForm.metric,
            metric_qualifier:  ruleForm.metric === 'revenue_type_attainment'
                                 ? ruleForm.metric_qualifier
                                 : null,
            operator:          ruleForm.operator,
            threshold_value:   ruleForm.threshold_value,
            threshold_value_2: ruleForm.operator === 'between' ? ruleForm.threshold_value_2 : null,
            multiplier_value:  ruleForm.multiplier_value,
            condition_logic:   ruleForm.condition_logic,
          }),
        }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error?.message || body?.detail || `Update failed (${res.status})`);
      }
      await loadPlanDetail(selectedPlan.kicker_plan_id);
      resetRuleForm();
      setShowRuleForm(false);
      showToast('Rule updated.');
    } catch (error) {
      setRuleError(error.message || 'Unable to update rule.');
    } finally {
      setRuleSaving(false);
    }
  }, [editingRuleId, loadPlanDetail, resetRuleForm, ruleForm, selectedPlan, showToast, validateRuleForm]);

  const openEditRule = useCallback((rule) => {
    setEditingRuleId(rule.kicker_rule_id);
    setRuleForm({
      rule_name:         rule.rule_name || '',
      rule_sequence:     String(rule.rule_sequence ?? '1'),
      metric:            rule.metric || 'attainment_pct',
      metric_qualifier:  rule.metric_qualifier || '',
      operator:          rule.operator || 'gte',
      threshold_value:   rule.threshold_value != null ? String(rule.threshold_value) : '',
      threshold_value_2: rule.threshold_value_2 != null ? String(rule.threshold_value_2) : '',
      multiplier_value:  rule.multiplier_value != null ? String(rule.multiplier_value) : '',
      condition_logic:   rule.condition_logic || 'AND',
    });
    setRuleError('');
    setShowRuleForm(true);
  }, []);

  // ---------------------------------------------------------------------------
  // Delete rule (DELETE — raw fetch)
  // ---------------------------------------------------------------------------

  const deleteRule = useCallback(async (ruleId) => {
    if (!selectedPlan) return;
    setRuleError('');
    try {
      const res = await fetch(
        `/api/v1/kicker-plans/${selectedPlan.kicker_plan_id}/rules/${ruleId}`,
        { method: 'DELETE', credentials: 'include' }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error?.message || body?.detail || `Delete failed (${res.status})`);
      }
      setConfirmDeleteRuleId(null);
      await loadPlanDetail(selectedPlan.kicker_plan_id);
      showToast('Rule removed.');
    } catch (error) {
      setRuleError(error.message || 'Unable to remove rule.');
    }
  }, [loadPlanDetail, selectedPlan, showToast]);

  // ---------------------------------------------------------------------------
  // Results tab
  // ---------------------------------------------------------------------------

  const loadResults = useCallback(async () => {
    if (!runIdInput.trim()) { setResultsError('Calculation run ID is required.'); return; }
    setResultsLoading(true);
    setResultsError('');
    setResults([]);
    try {
      let url = `/api/v1/kicker-results?calculation_run_id=${encodeURIComponent(runIdInput.trim())}`;
      if (positionIdInput.trim()) url += `&position_id=${encodeURIComponent(positionIdInput.trim())}`;
      const data = await apiGet(url);
      setResults(Array.isArray(data) ? data : data?.data || []);
    } catch (error) {
      setResultsError(getErrorMessage(error, 'Unable to load kicker results.'));
    } finally {
      setResultsLoading(false);
    }
  }, [positionIdInput, runIdInput]);

  // ---------------------------------------------------------------------------
  // Derived
  // ---------------------------------------------------------------------------

  const componentNameById = useMemo(
    () => components.reduce((acc, c) => {
      acc[String(c.plan_component_id)] = c.role_code || c.role_id || `Component ${c.plan_component_id}`;
      return acc;
    }, {}),
    [components]
  );

  const nextRuleSequence = useMemo(
    () => planRules.length > 0
      ? Math.max(...planRules.map((r) => r.rule_sequence || 0)) + 1
      : 1,
    [planRules]
  );

  // ---------------------------------------------------------------------------
  // Feature gate / auth guard
  // ---------------------------------------------------------------------------

  if (featureUnavailable) return <GateStatusBanner feature="Kicker / Multiplier Plans" />;

  if (!user) {
    return (
      <section className="flex flex-col gap-6">
        <h2 className="text-tok-xl font-semibold text-[rgb(var(--color-text))]">Kicker Plans</h2>
        <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
          Authentication required.
        </div>
      </section>
    );
  }

  // ---------------------------------------------------------------------------
  // Plan detail view
  // ---------------------------------------------------------------------------

  if (selectedPlan) {
    const state     = deriveState(selectedPlan);
    const isDraft   = state === 'draft';
    const needsRule = isDraft && planRules.length === 0;

    return (
      <section className="flex flex-col gap-6">
        {toast && (
          <div role="status" className="fixed bottom-4 right-4 z-50 rounded-lg bg-[rgb(var(--color-primary))] px-4 py-3 text-tok-sm text-white shadow-lg">
            {toast}
          </div>
        )}

        <header className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <button
              type="button"
              className={btnSecondary}
              onClick={() => {
                setSelectedPlan(null);
                setPlanRules([]);
                setActionError('');
                setShowRuleForm(false);
                resetRuleForm();
              }}
            >
              ← Back to kicker plans
            </button>
            <h2 className="mt-3 text-tok-xl font-semibold text-[rgb(var(--color-text))]">
              {selectedPlan.plan_name}
            </h2>
            <p className="mt-1 text-tok-sm text-[rgb(var(--color-text-muted))]">
              {labelFor(APPLIES_TO_OPTIONS, selectedPlan.applies_to)} ·
              {componentNameById[String(selectedPlan.plan_component_id)] || `Component ${selectedPlan.plan_component_id}`} ·
              Effective {fmtDate(selectedPlan.effective_from)}{selectedPlan.effective_to ? ` → ${fmtDate(selectedPlan.effective_to)}` : ' (open-ended)'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <StatusPill status={state} />
            {isDraft && (
              <button
                type="button"
                className={btnSecondary}
                onClick={() => openEditForm(selectedPlan)}
              >
                Edit plan
              </button>
            )}
          </div>
        </header>

        {actionError && (
          <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
            {actionError}
          </div>
        )}

        {/* Plan detail */}
        <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-5 flex flex-col gap-3 shadow-sm">
          <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Plan details</h3>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-tok-sm">
            <div>
              <dt className="text-[rgb(var(--color-text-muted))] font-medium">Applies to</dt>
              <dd className="text-[rgb(var(--color-text))]">{labelFor(APPLIES_TO_OPTIONS, selectedPlan.applies_to)}</dd>
            </div>
            <div>
              <dt className="text-[rgb(var(--color-text-muted))] font-medium">Plan component</dt>
              <dd className="text-[rgb(var(--color-text))]">{componentNameById[String(selectedPlan.plan_component_id)] || selectedPlan.plan_component_id}</dd>
            </div>
            <div>
              <dt className="text-[rgb(var(--color-text-muted))] font-medium">Effective from</dt>
              <dd className="text-[rgb(var(--color-text))]">{fmtDate(selectedPlan.effective_from)}</dd>
            </div>
            <div>
              <dt className="text-[rgb(var(--color-text-muted))] font-medium">Effective to</dt>
              <dd className="text-[rgb(var(--color-text))]">{selectedPlan.effective_to ? fmtDate(selectedPlan.effective_to) : 'Open-ended'}</dd>
            </div>
            {selectedPlan.plan_description && (
              <div className="sm:col-span-2">
                <dt className="text-[rgb(var(--color-text-muted))] font-medium">Description</dt>
                <dd className="text-[rgb(var(--color-text))]">{selectedPlan.plan_description}</dd>
              </div>
            )}
          </dl>
        </div>

        {/* ── Edit plan form (inline) ── */}
        {showPlanForm && editingPlanId === selectedPlan.kicker_plan_id && (
          <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-5 flex flex-col gap-4 shadow-sm">
            <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Edit plan</h3>
            {planFormError && (
              <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
                {planFormError}
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className={labelClass + ' sm:col-span-2'}>
                Plan name <span className="text-[rgb(var(--color-danger))]">*</span>
                <input type="text" className={inputClass} value={planForm.plan_name}
                  onChange={(e) => updatePlanForm('plan_name', e.target.value)} />
              </label>
              <label className={labelClass}>
                Applies to <span className="text-[rgb(var(--color-danger))]">*</span>
                <select className={inputClass} value={planForm.applies_to}
                  onChange={(e) => updatePlanForm('applies_to', e.target.value)}>
                  {APPLIES_TO_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </label>
              <label className={labelClass}>
                Effective from <span className="text-[rgb(var(--color-danger))]">*</span>
                <input type="date" className={inputClass} value={planForm.effective_from}
                  onChange={(e) => updatePlanForm('effective_from', e.target.value)} />
              </label>
              <label className={labelClass}>
                Effective to
                <input type="date" className={inputClass} value={planForm.effective_to}
                  onChange={(e) => updatePlanForm('effective_to', e.target.value)} />
              </label>
              <label className={labelClass + ' sm:col-span-2'}>
                Description
                <textarea className={inputClass + ' min-h-16 resize-y'} rows={2}
                  value={planForm.plan_description}
                  onChange={(e) => updatePlanForm('plan_description', e.target.value)} />
              </label>
            </div>
            <div className="flex items-center gap-2">
              <button type="button" className={btnPrimary} onClick={updatePlan} disabled={planSaving}>
                {planSaving ? 'Saving…' : 'Save changes'}
              </button>
              <button type="button" className={btnSecondary}
                onClick={() => { setShowPlanForm(false); resetPlanForm(); }}
                disabled={planSaving}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* ── Rules section ── */}
        <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-5 flex flex-col gap-4 shadow-sm">
          <div className="flex items-start justify-between gap-2 flex-wrap">
            <div>
              <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">
                Kicker rules
                {planRules.length > 0 && (
                  <span className="ml-2 inline-block px-2 py-0.5 rounded-full text-tok-xs font-medium bg-[rgb(var(--color-surface-muted))] text-[rgb(var(--color-text-muted))]">
                    {planRules.length}
                  </span>
                )}
              </h3>
              <p className="mt-1 text-tok-sm text-[rgb(var(--color-text-muted))]">
                At least one rule required before submission.
                Rules are evaluated in sequence order and combined with AND / OR logic.
                The kicker multiplier fires when the combined condition is met.
              </p>
            </div>
            {isDraft && !showRuleForm && (
              <button
                type="button"
                className={btnSecondary}
                onClick={() => {
                  resetRuleForm();
                  setRuleForm((prev) => ({ ...prev, rule_sequence: String(nextRuleSequence) }));
                  setShowRuleForm(true);
                }}
              >
                + Add rule
              </button>
            )}
          </div>

          {needsRule && (
            <div className="rounded-lg bg-[rgb(var(--color-warning))]/10 border border-[rgb(var(--color-warning))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-warning))]">
              No rules defined yet. Add at least one rule before submitting this plan for approval.
            </div>
          )}

          {ruleError && (
            <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
              {ruleError}
            </div>
          )}

          {/* Rule add / edit form */}
          {showRuleForm && (
            <div className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))]/40 p-4 flex flex-col gap-4">
              <h4 className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">
                {editingRuleId ? 'Edit rule' : 'New rule'}
              </h4>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <label className={labelClass + ' sm:col-span-2'}>
                  Rule name <span className="text-[rgb(var(--color-danger))]">*</span>
                  <input type="text" className={inputClass} value={ruleForm.rule_name}
                    onChange={(e) => updateRuleForm('rule_name', e.target.value)}
                    placeholder="e.g. New logo ≥ 120% attainment" />
                </label>
                <label className={labelClass}>
                  Sequence <span className="text-[rgb(var(--color-danger))]">*</span>
                  <input type="number" min="1" step="1" className={inputClass}
                    value={ruleForm.rule_sequence}
                    onChange={(e) => updateRuleForm('rule_sequence', e.target.value)} />
                </label>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className={labelClass}>
                  Metric <span className="text-[rgb(var(--color-danger))]">*</span>
                  <select className={inputClass} value={ruleForm.metric}
                    onChange={(e) => { updateRuleForm('metric', e.target.value); updateRuleForm('metric_qualifier', ''); }}>
                    {METRIC_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </label>
                {ruleForm.metric === 'revenue_type_attainment' && (
                  <label className={labelClass}>
                    Revenue type <span className="text-[rgb(var(--color-danger))]">*</span>
                    <select className={inputClass} value={ruleForm.metric_qualifier}
                      onChange={(e) => updateRuleForm('metric_qualifier', e.target.value)}>
                      <option value="">Select revenue type…</option>
                      {REVENUE_TYPE_CODES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </label>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <label className={labelClass}>
                  Operator <span className="text-[rgb(var(--color-danger))]">*</span>
                  <select className={inputClass} value={ruleForm.operator}
                    onChange={(e) => { updateRuleForm('operator', e.target.value); updateRuleForm('threshold_value_2', ''); }}>
                    {OPERATOR_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </label>
                <label className={labelClass}>
                  Threshold <span className="text-[rgb(var(--color-danger))]">*</span>
                  <input type="number" step="0.000001" className={inputClass}
                    value={ruleForm.threshold_value}
                    onChange={(e) => updateRuleForm('threshold_value', e.target.value)}
                    placeholder={
                      ruleForm.metric === 'attainment_pct' || ruleForm.metric === 'revenue_type_attainment'
                        ? 'e.g. 1.2000 for 120%'
                        : 'e.g. 50000'
                    } />
                </label>
                {ruleForm.operator === 'between' && (
                  <label className={labelClass}>
                    Upper threshold <span className="text-[rgb(var(--color-danger))]">*</span>
                    <input type="number" step="0.000001" className={inputClass}
                      value={ruleForm.threshold_value_2}
                      onChange={(e) => updateRuleForm('threshold_value_2', e.target.value)}
                      placeholder="Upper bound" />
                  </label>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className={labelClass}>
                  Multiplier <span className="text-[rgb(var(--color-danger))]">*</span>
                  <input type="number" step="0.000001" min="0.000001" className={inputClass}
                    value={ruleForm.multiplier_value}
                    onChange={(e) => updateRuleForm('multiplier_value', e.target.value)}
                    placeholder="e.g. 1.1500 for a 15% boost" />
                  <span className="mt-1 text-tok-xs text-[rgb(var(--color-text-muted))]">
                    1.0 = no change · 1.15 = +15% · 0.90 = −10%
                  </span>
                </label>
                <label className={labelClass}>
                  Condition logic <span className="text-[rgb(var(--color-danger))]">*</span>
                  <select className={inputClass} value={ruleForm.condition_logic}
                    onChange={(e) => updateRuleForm('condition_logic', e.target.value)}>
                    {CONDITION_LOGIC_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                  <span className="mt-1 text-tok-xs text-[rgb(var(--color-text-muted))]">
                    How this rule combines with earlier rules in the sequence.
                  </span>
                </label>
              </div>

              <div className="flex items-center gap-2">
                <button type="button" className={btnPrimary}
                  onClick={editingRuleId ? updateRule : addRule}
                  disabled={ruleSaving}>
                  {ruleSaving ? 'Saving…' : editingRuleId ? 'Save rule' : 'Add rule'}
                </button>
                <button type="button" className={btnSecondary}
                  onClick={() => { setShowRuleForm(false); resetRuleForm(); }}
                  disabled={ruleSaving}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Rules table */}
          {planRules.length > 0 && (
            <div className="overflow-x-auto rounded-lg border border-[rgb(var(--color-border))]">
              <table className="min-w-full text-tok-sm border-collapse">
                <thead>
                  <tr>
                    <th className={thClass}>#</th>
                    <th className={thClass}>Rule name</th>
                    <th className={thClass}>Metric</th>
                    <th className={thClass}>Condition</th>
                    <th className={thClass}>Multiplier</th>
                    <th className={thClass}>Logic</th>
                    {isDraft && <th className={thClass} aria-label="Actions" />}
                  </tr>
                </thead>
                <tbody>
                  {[...planRules]
                    .sort((a, b) => (a.rule_sequence ?? 0) - (b.rule_sequence ?? 0))
                    .map((rule) => (
                      <tr key={rule.kicker_rule_id} className="hover:bg-[rgb(var(--color-surface-muted))]">
                        <td className={tdClass + ' font-medium'}>{rule.rule_sequence}</td>
                        <td className={tdClass + ' font-medium'}>{rule.rule_name}</td>
                        <td className={tdClass}>
                          {labelFor(METRIC_OPTIONS, rule.metric)}
                          {rule.metric_qualifier && (
                            <span className="ml-1 text-[rgb(var(--color-text-muted))]">
                              ({labelFor(REVENUE_TYPE_CODES, rule.metric_qualifier) || rule.metric_qualifier})
                            </span>
                          )}
                        </td>
                        <td className={tdClass}>
                          {labelFor(OPERATOR_OPTIONS, rule.operator)} {formatThreshold(rule)}
                        </td>
                        <td className={tdClass + ' font-medium'}>{fmtMultiplier(rule.multiplier_value)}</td>
                        <td className={tdClass}>
                          <span className={`inline-block px-2 py-0.5 rounded text-tok-xs font-medium ${
                            rule.condition_logic === 'AND'
                              ? 'bg-[rgb(var(--color-info))]/10 text-[rgb(var(--color-info))]'
                              : 'bg-[rgb(var(--color-warning))]/10 text-[rgb(var(--color-warning))]'
                          }`}>
                            {rule.condition_logic}
                          </span>
                        </td>
                        {isDraft && (
                          <td className={tdClass}>
                            <div className="flex items-center gap-3">
                              <button type="button"
                                className="text-tok-sm text-[rgb(var(--color-primary))] hover:underline"
                                onClick={() => openEditRule(rule)}>
                                Edit
                              </button>
                              {confirmDeleteRuleId === rule.kicker_rule_id ? (
                                <span className="flex items-center gap-1 text-tok-xs">
                                  <button type="button" className={btnDanger + ' !px-2 !py-0.5 !text-tok-xs'}
                                    onClick={() => deleteRule(rule.kicker_rule_id)}>
                                    Confirm
                                  </button>
                                  <button type="button" className={btnSecondary + ' !px-2 !py-0.5 !text-tok-xs'}
                                    onClick={() => setConfirmDeleteRuleId(null)}>
                                    Cancel
                                  </button>
                                </span>
                              ) : (
                                <button type="button"
                                  className="text-tok-sm text-[rgb(var(--color-danger))] hover:underline"
                                  onClick={() => setConfirmDeleteRuleId(rule.kicker_rule_id)}>
                                  Remove
                                </button>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* ── Approval panel ── */}
        {detailLoading ? (
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Loading…</p>
        ) : (
          <ApprovalPanel
            variant="plan"
            currentState={state}
            submittedBy={selectedPlan.submitted_by ?? null}
            currentUserId={currentUserId}
            onSubmit={state === 'draft' ? submitPlan : null}
            onApprove={state === 'pending_approval' ? approvePlan : null}
            onReject={null}
            approvedBy={selectedPlan.approved_by ?? null}
            approvedAt={selectedPlan.approved_at ?? null}
            loading={actionLoading}
          />
        )}
      </section>
    );
  }

  // ---------------------------------------------------------------------------
  // Main tabbed view
  // ---------------------------------------------------------------------------

  return (
    <section className="flex flex-col gap-6">
      {toast && (
        <div role="status" className="fixed bottom-4 right-4 z-50 rounded-lg bg-[rgb(var(--color-primary))] px-4 py-3 text-tok-sm text-white shadow-lg">
          {toast}
        </div>
      )}

      <header>
        {mode === 'wizard' && (
          <p className="text-tok-xs font-semibold uppercase tracking-widest text-[rgb(var(--color-text-muted))] mb-1">
            Stage 8 · P3-02
          </p>
        )}
        <h2 className="text-tok-xl font-semibold text-[rgb(var(--color-text))]">Kicker / Multiplier Plans</h2>
        <p className="mt-1 text-tok-sm text-[rgb(var(--color-text-muted))]">
          Configure conditional multipliers that boost a rep's payout when performance thresholds are crossed.
          Each kicker plan belongs to a plan component and requires at least one rule before approval.
        </p>
      </header>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-[rgb(var(--color-border))]">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key)}
            className={[
              'px-4 py-2 text-tok-sm font-medium border-b-2 -mb-px transition-colors',
              activeTab === tab.key
                ? 'border-[rgb(var(--color-primary))] text-[rgb(var(--color-primary))]'
                : 'border-transparent text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))]',
            ].join(' ')}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Kicker Plans tab ── */}
      {activeTab === 'plans' && (
        <div className="flex flex-col gap-4">

          {/* Component selector — hidden when embedded (spec §4.8) */}
          <div className="flex items-end gap-3 flex-wrap">
            {!embedded && <label className={labelClass} style={{ flex: '1', minWidth: '240px' }}>
              Plan component
              {componentsLoading && <span className="ml-2 text-tok-xs text-[rgb(var(--color-text-muted))]">Loading…</span>}
              <select
                className={inputClass}
                value={selectedComponentId}
                onChange={(e) => {
                  setSelectedComponentId(e.target.value);
                  setPlans([]);
                  setShowPlanForm(false);
                  resetPlanForm();
                }}
              >
                <option value="">Select a plan component…</option>
                {components.map((c) => (
                  <option key={c.plan_component_id} value={c.plan_component_id}>
                    {c.role_code || c.role_id || `Component ${c.plan_component_id}`}
                    {c.segment_code ? ` — ${c.segment_code}` : ''}
                  </option>
                ))}
              </select>
            </label>}
            {selectedComponentId && !showPlanForm && (
              <button
                type="button"
                className={btnPrimary}
                onClick={() => {
                  resetPlanForm();
                  setShowPlanForm(true);
                }}
              >
                + New kicker plan
              </button>
            )}
          </div>

          {componentsError && (
            <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
              {componentsError}
            </div>
          )}

          {/* Plan create form */}
          {showPlanForm && !editingPlanId && (
            <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-5 flex flex-col gap-4 shadow-sm">
              <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">New kicker plan</h3>
              {planFormError && (
                <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
                  {planFormError}
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className={labelClass + ' sm:col-span-2'}>
                  Plan name <span className="text-[rgb(var(--color-danger))]">*</span>
                  <input type="text" className={inputClass} value={planForm.plan_name}
                    onChange={(e) => updatePlanForm('plan_name', e.target.value)}
                    placeholder="e.g. H2 New Logo Kicker" />
                </label>
                <label className={labelClass}>
                  Applies to <span className="text-[rgb(var(--color-danger))]">*</span>
                  <select className={inputClass} value={planForm.applies_to}
                    onChange={(e) => updatePlanForm('applies_to', e.target.value)}>
                    {APPLIES_TO_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </label>
                <label className={labelClass}>
                  Effective from <span className="text-[rgb(var(--color-danger))]">*</span>
                  <input type="date" className={inputClass} value={planForm.effective_from}
                    onChange={(e) => updatePlanForm('effective_from', e.target.value)} />
                </label>
                <label className={labelClass}>
                  Effective to
                  <input type="date" className={inputClass} value={planForm.effective_to}
                    onChange={(e) => updatePlanForm('effective_to', e.target.value)} />
                </label>
                <label className={labelClass + ' sm:col-span-2'}>
                  Description
                  <textarea className={inputClass + ' min-h-16 resize-y'} rows={2}
                    value={planForm.plan_description}
                    onChange={(e) => updatePlanForm('plan_description', e.target.value)}
                    placeholder="Optional — describe when this kicker fires" />
                </label>
              </div>
              <div className="flex items-center gap-2">
                <button type="button" className={btnPrimary} onClick={createPlan} disabled={planSaving}>
                  {planSaving ? 'Creating…' : 'Create plan'}
                </button>
                <button type="button" className={btnSecondary}
                  onClick={() => { setShowPlanForm(false); resetPlanForm(); }}
                  disabled={planSaving}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Plans list */}
          {selectedComponentId && (
            <>
              {plansLoading && (
                <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Loading kicker plans…</p>
              )}
              {plansError && (
                <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
                  {plansError}
                </div>
              )}
              {!plansLoading && !plansError && plans.length === 0 && (
                <div className="rounded-lg bg-[rgb(var(--color-surface-muted))] border border-[rgb(var(--color-border))] px-5 py-8 text-center">
                  <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
                    No kicker plans for this component.
                    Create one to define a multiplier that fires when performance thresholds are crossed.
                  </p>
                </div>
              )}
              {plans.length > 0 && (
                <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] shadow-sm overflow-x-auto">
                  <table className="min-w-full text-tok-sm border-collapse">
                    <thead>
                      <tr>
                        <th className={thClass}>Plan name</th>
                        <th className={thClass}>Applies to</th>
                        <th className={thClass}>Effective from</th>
                        <th className={thClass}>Effective to</th>
                        <th className={thClass}>Status</th>
                        <th className={thClass} aria-label="Actions" />
                      </tr>
                    </thead>
                    <tbody>
                      {plans.map((p) => (
                        <tr key={p.kicker_plan_id} className="hover:bg-[rgb(var(--color-surface-muted))]">
                          <td className={tdClass + ' font-medium'}>{p.plan_name}</td>
                          <td className={tdClass}>{labelFor(APPLIES_TO_OPTIONS, p.applies_to)}</td>
                          <td className={tdClass}>{fmtDate(p.effective_from)}</td>
                          <td className={tdClass}>{p.effective_to ? fmtDate(p.effective_to) : 'Open-ended'}</td>
                          <td className={tdClass}><StatusPill status={deriveState(p)} /></td>
                          <td className={tdClass}>
                            <button
                              type="button"
                              className="text-tok-sm text-[rgb(var(--color-primary))] hover:underline"
                              onClick={() => loadPlanDetail(p.kicker_plan_id)}
                            >
                              View
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}

          {!selectedComponentId && !componentsLoading && components.length === 0 && (
            <div className="rounded-lg bg-[rgb(var(--color-surface-muted))] border border-[rgb(var(--color-border))] px-5 py-8 text-center">
              <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
                No plan components found{period?.period_code ? ` for ${period.period_code}` : ''}.
                Plan components are created in the Plan Builder (Stage 8, Data Setup).
              </p>
            </div>
          )}
        </div>
      )}

      {/* ── Results tab ── */}
      {activeTab === 'results' && (
        <div className="flex flex-col gap-4">
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
            Review kicker rule evaluation outcomes for a completed calculation run.
            Each row shows whether the rule fired, the actual metric value, and payout before / after.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className={labelClass}>
              Calculation run ID <span className="text-[rgb(var(--color-danger))]">*</span>
              <input type="text" className={inputClass} value={runIdInput}
                onChange={(e) => setRunIdInput(e.target.value)}
                placeholder="UUID from calculation run" />
            </label>
            <label className={labelClass}>
              Position ID (optional)
              <input type="text" className={inputClass} value={positionIdInput}
                onChange={(e) => setPositionIdInput(e.target.value)}
                placeholder="Filter to a single position" />
            </label>
          </div>

          <div>
            <button type="button" className={btnPrimary} onClick={loadResults} disabled={resultsLoading}>
              {resultsLoading ? 'Loading…' : 'Load results'}
            </button>
          </div>

          {resultsError && (
            <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
              {resultsError}
            </div>
          )}

          {!resultsLoading && results.length === 0 && runIdInput && !resultsError && (
            <p className="text-tok-sm text-[rgb(var(--color-text-muted))] italic">
              No kicker results found for this run. Either no kicker plans were active for this period,
              or the run ID is incorrect.
            </p>
          )}

          {results.length > 0 && (
            <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] shadow-sm overflow-x-auto">
              <table className="min-w-full text-tok-sm border-collapse">
                <thead>
                  <tr>
                    <th className={thClass}>Position</th>
                    <th className={thClass}>Rule</th>
                    <th className={thClass}>Metric value</th>
                    <th className={thClass}>Fired</th>
                    <th className={thClass}>Multiplier applied</th>
                    <th className={thClass}>Payout before</th>
                    <th className={thClass}>Payout after</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((r) => (
                    <tr key={r.kicker_result_id} className="hover:bg-[rgb(var(--color-surface-muted))]">
                      <td className={tdClass + ' font-medium text-tok-xs'}>{r.position_id}</td>
                      <td className={tdClass}>{r.kicker_rule_id}</td>
                      <td className={tdClass}>{r.metric_value != null ? Number(r.metric_value).toFixed(4) : '—'}</td>
                      <td className={tdClass}>
                        <span className={`inline-block px-2 py-0.5 rounded text-tok-xs font-medium ${
                          r.rule_fired
                            ? 'bg-[rgb(var(--color-success))]/10 text-[rgb(var(--color-success))]'
                            : 'bg-[rgb(var(--color-surface-muted))] text-[rgb(var(--color-text-muted))]'
                        }`}>
                          {r.rule_fired ? 'Fired' : 'Not fired'}
                        </span>
                      </td>
                      <td className={tdClass + ' font-medium'}>{fmtMultiplier(r.multiplier_applied)}</td>
                      <td className={tdClass}>{fmtMoney(r.payout_before)}</td>
                      <td className={tdClass + (r.rule_fired ? ' font-medium text-[rgb(var(--color-success))]' : '')}>
                        {fmtMoney(r.payout_after)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
